import React from 'react';
import { Link } from '@tanstack/react-router';
import { motion } from 'motion/react';
import {
  ArrowRight, Check, ChevronRight, FileText, CalendarDays, Layers,
  Target, BarChart3, RefreshCw, Home,
} from 'lucide-react';
import { FaqAccordion } from '@/components/marketing/faq-accordion';
import { tracker360Faq } from '@/lib/tracker360-content';
import { PromoCarousel } from '@/components/marketing/promo-carousel';
import { ShareButton } from '@/components/app/share-button';

const reveal = {
  initial: { opacity: 0, y: 16 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-60px' },
  transition: { type: 'spring', stiffness: 220, damping: 28 },
};

function Section({ id, eyebrow, heading, lead, children }) {
  return (
    <motion.section id={id} {...reveal} className="mt-16 scroll-mt-20">
      {eyebrow ? (
        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
          {eyebrow}
        </p>
      ) : null}
      <h2 className="mt-2 text-[22px] font-extrabold tracking-tight sm:text-[26px]">{heading}</h2>
      {lead ? (
        <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{lead}</p>
      ) : null}
      <div className="mt-5">{children}</div>
    </motion.section>
  );
}

function NextStep({ text, to, label }) {
  return (
    <div className="glass mt-6 rounded-2xl border border-hairline p-4">
      <p className="text-[13px] leading-relaxed text-muted-foreground">{text}</p>
      <Link
        to={to}
        className="mt-2.5 inline-flex items-center gap-1 text-[13px] font-semibold text-primary transition-transform hover:translate-x-0.5 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
      >
        {label}
        <ArrowRight className="size-3.5" aria-hidden />
      </Link>
    </div>
  );
}

const STEPS = [
  { n: '01', t: 'Add your lectures', d: 'Import a coaching lecture planner as structured data, or pick chapters subject by subject.' },
  { n: '02', t: 'Choose your target', d: 'Set a target window from 15 days up to 4 years and the study days you actually have.' },
  { n: '03', t: 'Build your roadmap', d: 'Tracker 360 spreads the remaining lectures across those days with a realistic daily load.' },
  { n: '04', t: 'Follow your daily workload', d: 'Each day shows exactly which lectures and DPPs belong to it — no daily planning by hand.' },
  { n: '05', t: 'Track completion', d: 'Tick lectures off and watch the completion percentage and remaining work update live.' },
  { n: '06', t: 'Adjust and continue', d: 'Change the target date or chapter selection and regenerate the roadmap around what is left.' },
];

const PROBLEMS = [
  ['Fixed timetables go stale', 'A schedule written once cannot survive a changed coaching batch or a missed week.'],
  ['Missed lectures pile up', 'Backlog grows quietly until the syllabus feels impossible to finish.'],
  ['Unclear daily load', 'Without a number for “lectures per day”, every day feels either too light or hopeless.'],
  ['Subjects go unbalanced', 'One subject eats the week and the others stop moving entirely.'],
  ['Revision gets postponed', 'There is no reserved space in the week, so revision is always “next week”.'],
  ['No completion visibility', 'A timetable tells you what to do today, not how much of the syllabus is actually done.'],
];

const FEATURES = [
  {
    id: 'lecture-planner',
    icon: FileText,
    h: 'Turn a Lecture Planner Into a Roadmap',
    p: 'Convert a coaching lecture planner into structured lecture data — chapter titles, lecture counts and DPP tags — then let Tracker 360 lay it out on a calendar. The tutorial inside step 1 walks through the exact conversion.',
    flow: ['Lecture planner', 'Structured lecture data', 'Personal timeline', 'Daily roadmap'],
    cta: { to: '/study-planner', label: 'Open the daily study planner' },
  },
  {
    id: 'timeline',
    icon: CalendarDays,
    h: 'Your Target Date Becomes a Daily Plan',
    p: 'Tracker 360 takes your remaining lectures, your available study days and your playback speed, then works out the daily workload needed to reach your target date — instead of leaving that arithmetic to you.',
    flow: ['Target date', 'Remaining lectures', 'Available days', 'Lectures per day'],
    cta: { to: '/jee-preparation-tracker', label: 'See the JEE Preparation Tracker' },
  },
  {
    id: 'backlog',
    icon: RefreshCw,
    h: 'Stop Carrying the Same Backlog Every Week',
    p: 'Select only the chapters that are still pending. Everything you have already finished stays out of the schedule, so the roadmap reflects real remaining work rather than the full syllabus.',
    flow: ['Backlog', 'Remaining work', 'Daily allocation', 'Progress', 'Completion'],
    cta: { to: '/revision-tracker', label: 'Explore the Revision Planner' },
  },
  {
    id: 'interleaving',
    icon: Layers,
    h: 'Keep Multiple Subjects Moving',
    p: 'Physics, Chemistry, Maths and Biology are interleaved into one balanced day. You can also add a custom subject. The point is simple: no single subject is allowed to consume the whole schedule.',
    flow: ['Physics', 'Chemistry', 'Maths', 'Biology'],
    cta: { to: '/questions', label: 'Open the Question Tracker' },
  },
  {
    id: 'testing',
    icon: Target,
    h: 'Make Space for Revision and Testing',
    p: 'Every 14th day and every Sunday is kept free of new lectures, so there is room for a full-length test, DPP clearance or revision without pushing the roadmap out of shape.',
    flow: ['Day 13 lectures', 'Day 14 test', 'Sunday revision'],
    cta: { to: '/test-tracker', label: 'Use the test tracker' },
  },
  {
    id: 'analytics',
    icon: BarChart3,
    h: 'Know How Much Work Is Actually Left',
    p: 'A live completion bar, lectures done versus lectures remaining, and progress against your target window. Measurable progress is more useful than a timetable you cannot audit.',
    flow: ['Completed', 'Remaining', 'Target window'],
    cta: { to: '/tests/performance', label: 'Explore performance tracking' },
  },
];


const WHY = [
  'Less timetable guesswork — the daily number is calculated for you',
  'Clearer daily workload across every selected subject',
  'Backlog becomes visible instead of vague',
  'More realistic target dates you can sanity-check',
  'Revision and test days built into the week',
  'Progress you can measure, not estimate',
];

const AUDIENCES = [
  ['Starting from scratch', 'Plan a full syllabus from day one with a realistic finish date.'],
  ['Carrying lecture backlogs', 'Select only pending chapters and rebuild the plan around them.'],
  ['Following a coaching batch', 'Convert the batch lecture planner into your own day-by-day roadmap.'],
  ['Droppers', 'Long horizons, heavy backlog and fixed test days in one schedule.'],
  ['Class 11 students', 'Keep pace with new chapters without letting older ones slip.'],
  ['Class 12 students', 'Balance board syllabus with competitive preparation.'],
  ['Competitive exam aspirants', 'Plan around a target exam date and protected test days.'],
  ['Racing a deadline', 'Compress the remaining syllabus into the days you actually have left.'],
];

const EXAMS = [
  ['JEE Preparation', '/jee'],
  ['NEET Preparation', '/neet'],
  ['CBSE Study Planning', '/cbse'],
  ['ISC Planning', '/isc'],
  ['ICSE Planning', '/icse'],
  ['KCET Preparation', '/kcet'],
  ['MHT CET Preparation', '/mht-cet'],
];

const COMPARE = [
  ['Fixed schedule', 'Roadmap rebuilt from remaining work'],
  ['Manual workload maths', 'Daily load calculated from your target date'],
  ['Backlog stays hidden', 'Pending chapters drive the plan'],
  ['Revision postponed', 'Sundays and every 14th day kept free'],
  ['Generic timetable', 'Built from your own lecture list'],
  ['No progress visibility', 'Live completion analytics'],
];

const LOOP = [
  ['Plan', '/tracker-360'],
  ['Learn', '/study-planner'],
  ['Track', '/jee-preparation-tracker'],
  ['Revise', '/revision-tracker'],
  ['Practice', '/questions'],
  ['Test', '/test-tracker'],
  ['Analyze', '/tests/performance'],
  ['Improve', '/toolkit'],
];

const FAQ = tracker360Faq;

export default function Tracker360Landing({ onStartTracking }) {
  return (
    <div className="relative">
      <div aria-hidden className="aurora pointer-events-none fixed inset-0 -z-10" />
      <main className="mx-auto max-w-3xl px-5 pb-20 pt-6">
        {/* Breadcrumb */}
        <nav aria-label="Breadcrumb">
          <ol className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
            <li>
              <Link to="/" className="inline-flex items-center gap-1 hover:text-foreground">
                <Home className="size-3" aria-hidden /> Home
              </Link>
            </li>
            <li aria-hidden><ChevronRight className="size-3" /></li>
            <li aria-current="page" className="font-semibold text-foreground">Tracker 360</li>
          </ol>
        </nav>

        {/* Hero */}
        <motion.header
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ type: 'spring', stiffness: 240, damping: 28 }}
          className="mt-8"
        >
          <div className="flex items-center justify-between gap-3">
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
              Tracker 360 • Smart study planning
            </p>
            <ShareButton />
          </div>
          <h1 className="mt-3 text-[30px] font-extrabold leading-[1.12] tracking-tight sm:text-[40px]">
            Turn Your Lecture Backlog Into a Plan You Can Actually Finish
          </h1>
          <p className="mt-4 text-[15px] leading-relaxed text-muted-foreground">
            Tracker 360 turns your lecture planner, backlog and target date into a realistic
            day-by-day study roadmap. Instead of guessing what to study next, you get a structured
            plan built around your remaining lectures, subjects and preparation timeline.
          </p>

          <div className="mt-7 flex flex-col gap-3 sm:flex-row">
            <button
              type="button"
              onClick={onStartTracking}
              className="neural-gradient neural-halo relative isolate z-0 inline-flex h-13 items-center justify-center gap-2 rounded-full px-6 py-3.5 text-[15px] font-bold tracking-tight text-white transition-transform active:scale-95 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
            >
              Start Using Tracker 360
              <ArrowRight className="size-4" aria-hidden />
            </button>
            <a
              href="#how-it-works"
              className="glass inline-flex items-center justify-center rounded-full border border-hairline px-6 py-3.5 text-sm font-semibold transition-transform active:scale-95 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
            >
              See How It Works
            </a>
          </div>
          <p className="mt-3 text-xs text-muted-foreground">
            Free to start • No credit card required • Built for serious exam preparation
          </p>
        </motion.header>

        <div className="mt-10">
          <PromoCarousel />
        </div>

        <Section
          eyebrow="The idea"
          heading="What Is Tracker 360?"
          lead="Tracker 360 is a study planning and lecture roadmap system that helps students convert a large syllabus or coaching lecture backlog into a structured preparation plan."
        >
          <div className="space-y-3 text-sm leading-relaxed text-muted-foreground">
            <p>
              You tell it which chapters are still pending, how fast you watch lectures and when you
              want to be done. It returns a study timetable: a dated list of lectures and DPPs for
              every working day, with revision and test days already protected.
            </p>
            <p>
              It is built for students whose plan keeps breaking — a batch that moved, a week that
              was lost, a subject that stalled. Because the lecture planner is rebuilt from what is
              actually left, the plan stays honest instead of drifting further behind each week.
            </p>
            <p>
              The result is a preparation tracker that answers one question clearly every morning:
              what do I study today, and how much of the syllabus is still ahead of me?
            </p>
          </div>
        </Section>

        <Section eyebrow="The problem" heading="Why Normal Study Timetables Stop Working">
          <div className="grid gap-3 sm:grid-cols-2">
            {PROBLEMS.map(([t, d]) => (
              <div key={t} className="glass rounded-2xl border border-hairline p-4">
                <h3 className="text-sm font-bold tracking-tight">{t}</h3>
                <p className="mt-2 text-[13px] leading-relaxed text-muted-foreground">{d}</p>
              </div>
            ))}
          </div>
          <p className="mt-5 text-sm font-semibold">Tracker 360 turns the backlog into a roadmap.</p>
        </Section>

        <Section id="how-it-works" eyebrow="Process" heading="How Tracker 360 Works">
          <ol className="space-y-3">
            {STEPS.map((s) => (
              <motion.li key={s.n} {...reveal} className="glass rounded-2xl border border-hairline p-4">
                <div className="flex items-baseline gap-3">
                  <span className="text-[12px] font-black tabular-nums text-primary">{s.n}</span>
                  <h3 className="text-sm font-bold uppercase tracking-[0.12em]">{s.t}</h3>
                </div>
                <p className="mt-2 text-[13px] leading-relaxed text-muted-foreground">{s.d}</p>
              </motion.li>
            ))}
          </ol>
        </Section>

        {/* Feature showcase */}
        {FEATURES.map((f, i) => {
          const Icon = f.icon;
          return (
            <motion.section key={f.id} id={f.id} {...reveal} className="mt-16 scroll-mt-20">
              <div className="flex items-center gap-3">
                <span className="grid size-10 shrink-0 place-items-center rounded-2xl bg-surface">
                  <Icon className="size-4 text-primary" aria-hidden />
                </span>
                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                  Feature {String(i + 1).padStart(2, '0')}
                </p>
              </div>
              <h2 className="mt-3 text-[22px] font-extrabold tracking-tight sm:text-[26px]">{f.h}</h2>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{f.p}</p>

              <ul className="mt-4 flex flex-wrap items-center gap-2">
                {f.flow.map((node, idx) => (
                  <li key={node} className="flex items-center gap-2">
                    <span className="glass rounded-full border border-hairline px-3 py-1.5 text-[11px] font-semibold">
                      {node}
                    </span>
                    {idx < f.flow.length - 1 ? (
                      <ChevronRight className="size-3.5 text-muted-foreground" aria-hidden />
                    ) : null}
                  </li>
                ))}
              </ul>

              <Link
                to={f.cta.to}
                className="mt-4 inline-flex items-center gap-1 text-[13px] font-semibold text-primary transition-transform hover:translate-x-0.5 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
              >
                {f.cta.label}
                <ArrowRight className="size-3.5" aria-hidden />
              </Link>
            </motion.section>
          );
        })}

        <NextStep
          text="Your timetable is only the beginning. Once lectures are done, revision decides what actually stays."
          to="/revision-tracker"
          label="Explore Revision Planner"
        />


        <Section eyebrow="Benefits" heading="Why Students Use Tracker 360">
          <ul className="space-y-2.5 text-sm">
            {WHY.map((w) => (
              <li key={w} className="flex items-start gap-2.5">
                <span className="mt-0.5 grid size-5 shrink-0 place-items-center rounded-full bg-surface">
                  <Check className="size-3 text-primary" aria-hidden />
                </span>
                <span className="text-muted-foreground">{w}</span>
              </li>
            ))}
          </ul>
        </Section>

        <Section eyebrow="Who it's for" heading="Built for Different Preparation Situations">
          <div className="grid gap-3 sm:grid-cols-2">
            {AUDIENCES.map(([t, d]) => (
              <div key={t} className="glass rounded-2xl border border-hairline p-4">
                <h3 className="text-sm font-bold tracking-tight">{t}</h3>
                <p className="mt-2 text-[13px] leading-relaxed text-muted-foreground">{d}</p>
              </div>
            ))}
          </div>
        </Section>

        <Section
          eyebrow="Ecosystem"
          heading="One Planning System. Different Exam Goals."
          lead="The same roadmap engine sits behind every exam track on The Quantum Minds. Open the page closest to your goal to see how the workflow is shaped for it."
        >
          <div className="flex flex-wrap gap-2.5">
            {EXAMS.map(([label, to]) => (
              <Link
                key={to}
                to={to}
                className="glass inline-flex items-center rounded-full border border-hairline px-4 py-2 text-xs font-semibold transition-transform active:scale-95 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
              >
                {label}
              </Link>
            ))}
          </div>
        </Section>

        <Section eyebrow="Comparison" heading="Tracker 360 vs a Traditional Timetable">
          <div className="space-y-3">
            {COMPARE.map(([a, b]) => (
              <div key={a} className="glass grid gap-2 rounded-2xl border border-hairline p-4 sm:grid-cols-2">
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
                    Traditional timetable
                  </p>
                  <p className="mt-1 text-[13px] text-muted-foreground">{a}</p>
                </div>
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-primary">
                    Tracker 360
                  </p>
                  <p className="mt-1 text-[13px] font-medium">{b}</p>
                </div>
              </div>
            ))}
          </div>
        </Section>

        <Section eyebrow="Value" heading="Serious Planning Without an Expensive Study App">
          <div className="space-y-3 text-sm leading-relaxed text-muted-foreground">
            <p>
              You can start planning with Tracker 360 for free and without a card. The free tier is
              there so you can build a roadmap and see whether the pacing suits you before paying
              for anything.
            </p>
            <p>
              Extended access is part of the paid plan, alongside the rest of the platform. Plans
              and current prices are listed in one place so there is nothing to guess.
            </p>
            <p>
              Designed to give students more planning value without unnecessary complexity.
            </p>
          </div>
          <Link
            to="/pricing"
            className="mt-4 inline-flex items-center gap-1 text-[13px] font-semibold text-primary hover:translate-x-0.5 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
          >
            See current plans and pricing
            <ArrowRight className="size-3.5" aria-hidden />
          </Link>
        </Section>

        <Section
          eyebrow="Workflow"
          heading="Build Your Complete Preparation Loop"
          lead="Planning is one stage. Each link below opens the tool that carries your preparation to the next one."
        >
          <ol className="space-y-2">
            {LOOP.map(([label, to], i) => (
              <li key={label}>
                <Link
                  to={to}
                  className="glass flex items-center gap-3 rounded-2xl border border-hairline px-4 py-3 transition-transform hover:-translate-y-0.5 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
                >
                  <span className="text-[11px] font-black tabular-nums text-primary">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <span className="flex-1 text-sm font-semibold">{label}</span>
                  <ArrowRight className="size-3.5 text-muted-foreground" aria-hidden />
                </Link>
              </li>
            ))}
          </ol>
          <p className="mt-3 text-xs text-muted-foreground">
            The loop closes back into planning: once analysis shows the weak areas, rebuild the
            roadmap in Tracker 360 around them.
          </p>
        </Section>

        <Section heading="Frequently Asked Questions About Tracker 360">
          <FaqAccordion idPrefix="t360-faq" items={FAQ.map((f) => ({ q: f.q, a: f.a }))} />
        </Section>

        {/* Final CTA */}
        <motion.section {...reveal} className="glass relative mt-16 overflow-hidden rounded-[28px] border-hairline p-6 text-center">
          <div
            aria-hidden
            className="neural-gradient pointer-events-none absolute -top-16 left-1/2 size-40 -translate-x-1/2 rounded-full opacity-25 blur-3xl"
          />
          <h2 className="relative text-[20px] font-extrabold tracking-tight">
            Ready to Turn Your Backlog Into a Roadmap?
          </h2>
          <p className="relative mt-2 text-sm text-muted-foreground">
            Start with your target date, organize your remaining work and let Tracker 360 turn it
            into a plan you can follow.
          </p>
          <div className="relative mt-5 flex flex-col justify-center gap-3 sm:flex-row">
            <button
              type="button"
              onClick={onStartTracking}
              className="neural-gradient neural-halo relative isolate z-0 inline-flex items-center justify-center rounded-full px-6 py-3.5 text-[15px] font-bold tracking-tight text-white transition-transform active:scale-95 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
            >
              Start Using Tracker 360
            </button>
            <Link
              to="/"
              className="glass inline-flex items-center justify-center rounded-full border border-hairline px-6 py-3.5 text-sm font-semibold transition-transform active:scale-95 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
            >
              Explore The Quantum Minds
            </Link>
          </div>
        </motion.section>
      </main>
    </div>
  );
}
